import { TrustAPI } from '@/shared/modules/trust-api';
import { ComplianceFormTip } from '@/shared/modules/typescript-trustapi-client/models';
import { AppInsights } from '@/trust-admin-app/appInsights';
import store from '@/trust-admin-app/store';
import { EventEmitter } from 'events';
import { Inject, Options, Vue } from 'vue-property-decorator';
import ActionModal from './action-modal/index.vue';
import AdditionalDocumentsModal from '@/shared/components/additional-documents-modal/index.vue';

export interface MenuOption {
  id: number;
  menuText: string;
  menuAction: string;
  menuTarget: string;
  isDisabled?: boolean;
  icon?: string;
  showIcon?: boolean;
}

@Options({
  props: {
    practice: Object,
  },
  components: {
    ActionModal,
    AdditionalDocumentsModal,
  },
})
export default class DropdownActions extends Vue {
  private practice!: any;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private ai = new AppInsights();
  private showAdditionalDocumentsModal = false;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private dropdownRef = 'dropdown_' + this.guid();
  private buttonRef = 'button' + this.guid();
  private iconRef = 'icon' + this.guid();

  private menuOptions: MenuOption[] = [];
  private actionType = '';
  private showModal = false;

  private buttonClicked(): void {
    this.constructMenuOptions();
    const el = this.$refs[this.dropdownRef] as HTMLElement;
    const elBtn = this.$refs[this.buttonRef] as HTMLElement;
    el.classList.toggle('show');
    elBtn.classList.toggle('show');
  }

  private menuSelected(menuOption: any) {
    const el = this.$refs[this.dropdownRef] as HTMLElement;
    const elBtn = this.$refs[this.buttonRef] as HTMLElement;
    el.classList.remove('show');
    elBtn.classList.remove('show');

    if (menuOption.isDisabled) return;

    switch (menuOption.menuAction) {
      case 'open-modal':
        this.actionType = menuOption.menuTarget;
        if (this.actionType === 'file-uploads') {
          this.showAdditionalDocumentsModal = true;
        } else {
          this.showModal = true;
        }
        break;
      case 'view-pdf':
        switch (menuOption.menuTarget) {
          case 'pdf-part-a':
            this.viewPartA(this.practice);
            break;
          case 'pdf-part-b':
            this.viewPartB(this.practice);
            break;
          case 'pdf-eer':
            this.viewEer(this.practice);
            break;
        }
        break;
    }
  }

  mounted(): void {
    window.addEventListener('click', this.clickedElement);
  }

  private constructMenuOptions(): void {
    this.menuOptions = [];
    if (this.practice) {
      this.menuOptions.push({
        id: 1,
        menuText: 'View Part A',
        menuAction: 'view-pdf',
        menuTarget: 'pdf-part-a',
        isDisabled: !this.practice.trustAdminAction?.isEnableViewPartA,
      });
      this.menuOptions.push({
        id: 2,
        menuText: 'View Part B',
        menuAction: 'view-pdf',
        menuTarget: 'pdf-part-b',
        isDisabled: !this.practice.trustAdminAction?.isEnableViewPartB,
      });
      this.menuOptions.push({
        id: 3,
        menuText: 'View EE report',
        menuAction: 'view-pdf',
        menuTarget: 'pdf-eer',
        isDisabled: !this.practice.trustAdminAction?.isEnableViewEeReport,
      });
      this.menuOptions.push({
        id: 4,
        menuText: 'View file attachment',
        menuAction: 'open-modal',
        menuTarget: 'file-attachment',
        isDisabled: !this.practice.trustAdminAction?.isEnableViewFile,
      });
      this.menuOptions.push({
        id: 5,
        menuText: 'Additional documents',
        menuAction: 'open-modal',
        menuTarget: 'file-uploads',
        isDisabled: false,
        showIcon: this.practice?.hasAdditionalFiles,
        icon: 'fal fa-file ml-2',
      });
      this.menuOptions.push({
        id: 6,
        menuText: 'Resubmit Part A',
        menuAction: 'open-modal',
        menuTarget: 'resubmit-part-a',
        isDisabled: !this.practice.trustAdminAction?.isEnableResubmitPartA,
      });
      this.menuOptions.push({
        id: 7,
        menuText: 'Resubmit Part B',
        menuAction: 'open-modal',
        menuTarget: 'resubmit-part-b',
        isDisabled: !this.practice.trustAdminAction?.isEnableResubmitPartB,
      });
      this.menuOptions.push({
        id: 8,
        menuText: 'Not required',
        menuAction: 'open-modal',
        menuTarget: 'absolution',
        isDisabled: !this.practice.trustAdminAction?.isEnableNotRequired,
      });
    }
  }

  private async reloadData() {
    this.$emit('reloadData');
  }

  private clickedElement(e: MouseEvent): void | boolean {
    const el = this.$refs[this.dropdownRef] as HTMLElement;
    const elBtn = this.$refs[this.buttonRef] as HTMLElement;
    const elIcon = this.$refs[this.iconRef] as HTMLElement;
    const target: HTMLElement = e?.target as HTMLElement;
    if (el !== target && elBtn !== target && elIcon !== target) {
      el?.classList.remove('show');
      elBtn?.classList.remove('show');
    }
  }

  public guid(): string {
    const S4 = () => {
      // tslint:disable-next-line: no-bitwise
      return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    };
    // then to call it, plus stitch in '4' in the third group
    // tslint:disable-next-line: max-line-length
    return (S4() + S4() + '-' + S4() + '-4' + S4().substr(0, 3) + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase();
  }

  private async viewPartA(p: ComplianceFormTip): Promise<void> {
    if (p.partAPdfHasMultipleVersions) {
      // this.eventCtrl.emit('show-snackbar', 'error', 'Part A has multiple versions. Please download the PDF to view the latest version)');
      this.actionType = 'multiple-part-a';
      this.showModal = true;
    } else {
      try {
        const resp: any = await this.apiService.FileAtcsApi?.apiV2FileAtcsAdminGetPartAFilePartAIdGet(p.partA.id);
        const base64Data = resp.data.data.base64AsString;
        const base64Response = await fetch(`data:application/pdf;base64,${base64Data}`);
        const blob = await base64Response.blob();
        const documentURL = window.URL.createObjectURL(blob);
        window.open(documentURL, '_blank');
        setTimeout(() => {
          URL.revokeObjectURL(documentURL);
        }, 300000);
        Promise.resolve(0);
      } catch {
        this.eventCtrl.emit('show-snackbar', 'error', 'Error downloading Part A PDF');
        Promise.resolve(0);
      }
    }
  }

  private async viewPartB(p: ComplianceFormTip): Promise<void> {
    if (p.partBPdfHasMultipleVersions) {
      // this.eventCtrl.emit('show-snackbar', 'error', 'Part B has multiple versions. Please download the PDF to view the latest version)');
      this.actionType = 'multiple-part-b';
      this.showModal = true;
    } else {
      try {
        const resp: any = await this.apiService.FileAtcsApi?.apiV2FileAtcsAdminGetPartBFilePartBIdGet(p.partB.id);
        const base64Data = resp.data.data.base64AsString;
        const base64Response = await fetch(`data:application/pdf;base64,${base64Data}`);
        const blob = await base64Response.blob();
        const documentURL = window.URL.createObjectURL(blob);
        window.open(documentURL, '_blank');
        setTimeout(() => {
          URL.revokeObjectURL(documentURL);
        }, 300000);
        Promise.resolve(0);
      } catch {
        this.eventCtrl.emit('show-snackbar', 'error', 'Error downloading Part B PDF');
        Promise.resolve(0);
      }
    }
  }

  async viewEer(p: ComplianceFormTip): Promise<void> {
    if (p.eeReportPdfHasMultipleVersions) {
      // this.eventCtrl.emit('show-snackbar', 'error', 'EE report has multiple versions. Please download the PDF to view the latest version)');
      this.actionType = 'multiple-eer';
      this.showModal = true;
    } else {
      try {
        const resp: any = await this.apiService.FileAtcsApi?.apiV2FileAtcsAdminGetEeReportFileEeReportIdGet(
          p.eeReport.id,
        );
        const base64Data = resp.data.data.base64AsString;
        const base64Response = await fetch(`data:application/pdf;base64,${base64Data}`);
        const blob = await base64Response.blob();
        const documentURL = window.URL.createObjectURL(blob);
        window.open(documentURL, '_blank');
        setTimeout(() => {
          URL.revokeObjectURL(documentURL);
        }, 300000);
        Promise.resolve(0);
      } catch {
        this.eventCtrl.emit('show-snackbar', 'error', 'Error downloading EE report PDF');
        Promise.resolve(0);
      }
    }
  }
}
